/**
 * Capability list component.
 * Displays list of connector capabilities with enable/disable toggles.
 */

// React Imports
import { useState } from 'react';
import {
  List,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  Chip,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import type { ConnectorCapabilityDto } from '../../types/connector';

export interface CapabilityListProps {
  capabilities: ConnectorCapabilityDto[];
  enabledCapabilityIds?: string[];
  onToggle?: (capabilityId: string, enabled: boolean) => void;
  showDetails?: boolean;
  readOnly?: boolean;
}

/**
 * Capability list component.
 */
export const CapabilityList = ({
  capabilities,
  enabledCapabilityIds = [],
  onToggle,
  showDetails = true,
  readOnly = false,
}: CapabilityListProps) => {
  const [expandedCapability, setExpandedCapability] = useState<string | false>(false);

  const getRiskLevelColor = (riskLevel: string) => {
    switch (riskLevel.toLowerCase()) {
      case 'low':
        return 'success';
      case 'medium':
        return 'warning';
      case 'high':
      case 'critical':
        return 'error';
      default:
        return 'default';
    }
  };

  const getRiskLevelIcon = (riskLevel: string) => {
    switch (riskLevel.toLowerCase()) {
      case 'low':
        return <CheckCircleIcon fontSize="small" />;
      case 'medium':
        return <WarningIcon fontSize="small" />;
      case 'high':
      case 'critical':
        return <ErrorIcon fontSize="small" />;
      default:
        return undefined;
    }
  };

  const handleToggle = (capabilityId: string, currentEnabled: boolean) => {
    if (!readOnly && onToggle) {
      onToggle(capabilityId, !currentEnabled);
    }
  };

  const handleExpand = (capabilityId: string) => {
    setExpandedCapability(expandedCapability === capabilityId ? false : capabilityId);
  };

  if (capabilities.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No capabilities available
        </Typography>
      </Box>
    );
  }

  return (
    <List>
      {capabilities.map((capability) => {
        const isEnabled = enabledCapabilityIds.includes(capability.id);
        const isExpanded = expandedCapability === capability.id;

        return (
          <Accordion
            key={capability.id}
            expanded={isExpanded}
            onChange={() => showDetails && handleExpand(capability.id)}
            sx={{ mb: 1 }}
          >
            <AccordionSummary
              expandIcon={showDetails ? <ExpandMoreIcon /> : null}
              sx={{ '&.Mui-expanded': { minHeight: 56 } }}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1" fontWeight={500}>
                      {capability.name}
                    </Typography>
                    <Chip
                      label={capability.type}
                      size="small"
                      variant="outlined"
                      color={capability.type === 'Action' ? 'primary' : 'secondary'}
                    />
                    {capability.requiresApproval && (
                      <Chip
                        label="Requires Approval"
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                    )}
                    <Chip
                      icon={getRiskLevelIcon(capability.riskLevel)}
                      label={capability.riskLevel}
                      size="small"
                      color={getRiskLevelColor(capability.riskLevel) as any}
                      variant="outlined"
                    />
                  </Box>
                }
                secondary={
                  capability.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {capability.description}
                    </Typography>
                  )
                }
              />
              {!readOnly && (
                <ListItemSecondaryAction>
                  <Switch
                    edge="end"
                    checked={isEnabled}
                    onChange={() => handleToggle(capability.id, isEnabled)}
                    inputProps={{ 'aria-labelledby': `capability-${capability.id}` }}
                  />
                </ListItemSecondaryAction>
              )}
            </AccordionSummary>
            {showDetails && (
              <AccordionDetails>
                <Box sx={{ pl: 2, pr: 2, pb: 2 }}>
                  {capability.httpMethod && capability.endpointPath && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Endpoint
                      </Typography>
                      <Chip
                        label={`${capability.httpMethod} ${capability.endpointPath}`}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  )}
                  {capability.toolName && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Tool Name
                      </Typography>
                      <Typography variant="body2" fontFamily="monospace">
                        {capability.toolName}
                      </Typography>
                    </Box>
                  )}
                  {capability.toolDescription && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Tool Description
                      </Typography>
                      <Typography variant="body2">{capability.toolDescription}</Typography>
                    </Box>
                  )}
                  {capability.inputSchemaJson && capability.inputSchemaJson !== '{}' && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Input Schema
                      </Typography>
                      <Box
                        sx={{
                          p: 1,
                          bgcolor: 'action.hover',
                          borderRadius: 1,
                          fontFamily: 'monospace',
                          fontSize: '0.75rem',
                          overflow: 'auto',
                          maxHeight: 200,
                        }}
                      >
                        <pre style={{ margin: 0 }}>
                          {JSON.stringify(JSON.parse(capability.inputSchemaJson), null, 2)}
                        </pre>
                      </Box>
                    </Box>
                  )}
                </Box>
              </AccordionDetails>
            )}
          </Accordion>
        );
      })}
    </List>
  );
};
